import { useNewWorkIntent, isDirectWorkSearch, isNewWorkSearch, canSubmitDirectWork } from "@/lib/newWorkFlow";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContractsTab } from "@/components/contracts/ContractsTab";
import { Site360Dialog } from "@/components/customer/Site360Dialog";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { NewContractDialog } from "@/components/contracts/NewContractDialog";
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  CheckCircle2,
  Link2,
  Mail,
  MapPin,
  Phone,
  Plus,
  User,
} from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useRoute, useSearch } from "wouter";
import { resolveInstallationTitle } from "@/lib/installationContext";

const DATA_QUALITY_ROLES = ["admin", "admin_manager"];
const CUSTOMER_DETAIL_TABS = [
  "perfil",
  "contactos",
  "direcciones",
  "sedes",
  "instalaciones",
  "contratos",
  "relaciones",
  "calidad",
] as const;
type CustomerDetailTab = (typeof CUSTOMER_DETAIL_TABS)[number];

function isCustomerDetailTab(
  value: string | null,
  canSeeDQ: boolean,
): value is CustomerDetailTab {
  return (
    value != null &&
    CUSTOMER_DETAIL_TABS.includes(value as CustomerDetailTab) &&
    (value !== "calidad" || canSeeDQ)
  );
}

function getCustomerDetailTabFromLocation(
  location: string,
  canSeeDQ: boolean,
): CustomerDetailTab {
  const url = new URL(location, "http://localhost");
  const tab = url.searchParams.get("tab");
  return isCustomerDetailTab(tab, canSeeDQ) ? tab : "perfil";
}

function locationWithCustomerDetailTab(
  location: string,
  tab: CustomerDetailTab,
): string {
  const url = new URL(location, "http://localhost");
  if (tab === "perfil") url.searchParams.delete("tab");
  else url.searchParams.set("tab", tab);
  return `${url.pathname}${url.search}${url.hash}`;
}

const ENTITY_LABELS: Record<string, string> = {
  company: "Empresa",
  public_administration: "Administración pública",
  ute: "UTE",
  association_foundation: "Asociación / Fundación",
  self_employed: "Autónomo",
  individual: "Particular",
  organization: "Empresa",
  person: "Persona",
};

const ROLE_LABELS: Record<string, string> = {
  customer: "Cliente",
  supplier: "Proveedor",
  contracting_party: "Contratante",
  owner: "Propietario",
  operator: "Operador",
  occupier: "Ocupante",
  maintainer: "Mantenedor",
  primary: "Principal",
  billing: "Facturación",
  technical: "Técnico",
  sales: "Comercial",
  legal: "Legal",
  other: "Otro",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Activo",
  pending: "Pendiente",
  in_progress: "En curso",
  completed: "Completada",
  cancelled: "Cancelada",
  open: "Abierto",
  in_review: "En revisión",
  resolved: "Resuelto",
  wont_fix: "No corregir",
};

const CONTACT_SCOPE_LABELS: Record<"customer" | "contract" | "site" | "installation", string> = {
  customer: "Cliente",
  contract: "Contrato",
  site: "Sede",
  installation: "Instalación",
};

const CANONICAL_CONTACT_CATEGORIES = ["billing", "technical", "sales", "legal", "other"] as const;

function isNotFoundQueryError(error: unknown): boolean {
  if (error == null || typeof error !== "object") return false;
  const data = (error as { data?: unknown }).data;
  if (data == null || typeof data !== "object") return false;
  return (data as { code?: unknown }).code === "NOT_FOUND";
}

type DqCatalogEntry = {
  title: string;
  explanation: string;
  action: string;
  target: string; // pestaña o formulario al que llevar
  category: "bloqueo" | "incidencia" | "recomendacion";
  redBadge: boolean; // false para recomendaciones de prospecto
};

const DQ_CATALOG: Record<string, DqCatalogEntry> = {
  customer_missing_fiscal_address: {
    title: "Falta la dirección fiscal",
    explanation: "La entidad no tiene una dirección fiscal activa.",
    action: "Añadir dirección fiscal",
    target: "direcciones",
    category: "bloqueo",
    redBadge: true,
  },
  customer_missing_primary_contact: {
    title: "Falta un contacto principal",
    explanation: "La entidad no tiene un contacto principal asignado.",
    action: "Añadir contacto principal",
    target: "contactos",
    category: "incidencia",
    redBadge: false,
  },
  customer_missing_sales_owner: {
    title: "Falta asignar responsable comercial",
    explanation: "No se ha asignado un responsable comercial.",
    action: "Asignar responsable comercial",
    target: "perfil",
    category: "incidencia",
    redBadge: false,
  },
  customer_missing_website: {
    title: "No se ha informado la web (opcional)",
    explanation: "La entidad no tiene web registrada; añádela solo si existe.",
    action: "Añadir web si existe",
    target: "perfil",
    category: "recomendacion",
    redBadge: false,
  },
  customer_missing_general_phone: {
    title: "No se ha informado el teléfono general",
    explanation: "La entidad no tiene teléfono general registrado; añádelo solo si es necesario.",
    action: "Añadir teléfono si es necesario",
    target: "perfil",
    category: "recomendacion",
    redBadge: false,
  },
  customer_missing_billing_email: {
    title: "Falta el email de facturación",
    explanation: "La entidad no tiene email de facturación registrado.",
    action: "Añadir email de facturación",
    target: "perfil",
    category: "incidencia",
    redBadge: true,
  },
  customer_missing_valid_tax_id: {
    title: "Documento fiscal no válido",
    explanation: "El CIF/NIF/NIE no está validado o falta.",
    action: "Corregir documento fiscal",
    target: "perfil",
    category: "bloqueo",
    redBadge: true,
  },
  installation_missing_canonical_customer_partner: {
    title: "Instalación sin cliente canónico",
    explanation: "Hay instalaciones sin vínculo canónico con el cliente.",
    action: "Corregir vínculo de instalación",
    target: "instalaciones",
    category: "incidencia",
    redBadge: true,
  },
  installation_missing_site: {
    title: "Instalación sin sede",
    explanation: "Hay instalaciones sin sede asignada.",
    action: "Asignar sede a la instalación",
    target: "instalaciones",
    category: "incidencia",
    redBadge: false,
  },
};

const DQ_CATEGORIES: Record<DqCatalogEntry["category"], string> = {
  bloqueo: "Bloqueos",
  incidencia: "Incidencias",
  recomendacion: "Recomendaciones",
};

const DQ_SEVERITY_LABEL: Record<string, string> = {
  critical: "Crítica",
  high: "Alta",
  medium: "Media",
  low: "Baja",
};

const DQ_PENALTIES: Record<string, number> = {
  merged_partner_used_as_active: 40,
  customer_missing_valid_tax_id: 20,
  customer_missing_fiscal_address: 20,
  customer_missing_primary_contact: 10,
  customer_missing_billing_email: 10,
  customer_missing_sales_owner: 10,
  installation_missing_canonical_customer_partner: 20,
  installation_missing_site: 10,
  customer_missing_website: 3,
  customer_missing_general_phone: 3,
};

function resolveDqCategory(issueType: string): "bloqueo" | "incidencia" | "recomendacion" {
  return DQ_CATALOG[issueType]?.category ?? "incidencia";
}

function resolveDqEntry(issueType: string): DqCatalogEntry {
  return DQ_CATALOG[issueType] ?? {
    title: issueType.replaceAll("_", " "),
    explanation: "Revisa los datos de la entidad.",
    action: "Revisar",
    target: "perfil",
    category: "incidencia",
    redBadge: false,
  };
}

const PAYMENT_LABELS: Record<string, string> = {
  transfer: "Transferencia",
  check: "Cheque",
  direct_debit: "Domiciliación",
  cash: "Efectivo",
  other: "Otro",
};

const ADDRESS_LABELS: Record<string, string> = {
  fiscal: "Fiscal",
  billing: "Facturación",
  correspondence: "Correspondencia",
  delivery: "Entrega",
  other: "Otra",
};

const RELATIONSHIP_LABELS: Record<string, string> = {
  parent_of: "Matriz de",
  member_of_group: "Pertenece a grupo",
  member_of_ute: "Miembro de UTE",
  represented_by: "Representado por",
  successor_of: "Sucesor de",
  other: "Otra",
};

function label(map: Record<string, string>, value?: string | null) {
  if (!value) return "—";
  return map[value] ?? value;
}

function installationStatusLabel(status?: string | null) {
  if (!status) return "—";
  return STATUS_LABELS[status] ?? "Estado registrado";
}

function DetailRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm">{value ?? "—"}</span>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    critical: "destructive",
    high: "destructive",
    medium: "secondary",
    low: "outline",
  };
  const labels: Record<string, string> = {
    critical: "Crítica",
    high: "Alta",
    medium: "Media",
    low: "Baja",
  };
  return <Badge variant={map[severity] ?? "outline"}>{labels[severity] ?? severity}</Badge>;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    open: "destructive",
    in_review: "secondary",
    resolved: "default",
    wont_fix: "outline",
  };
  const labels: Record<string, string> = {
    open: "Abierto",
    in_review: "En revisión",
    resolved: "Resuelto",
    wont_fix: "No corregir",
  };
  return <Badge variant={map[status] ?? "outline"}>{labels[status] ?? "Estado registrado"}</Badge>;
}

function LifecycleBadge({ stage }: { stage: string | null }) {
  const labels: Record<string, string> = {
    prospect: "Prospecto",
    customer: "Cliente",
  };
  return <Badge variant={stage === "customer" ? "default" : "secondary"}>{labels[stage ?? ""] ?? "—"}</Badge>;
}

const EMPTY_INSTALLATION_FORM = {
  siteMode: "existing" as "existing" | "new",
  siteId: "",
  contractId: "",
  siteName: "",
  siteAddressLine1: "",
  siteCity: "",
  sitePostalCode: "",
  workOrderType: "breakdown" as "installation" | "breakdown" | "maintenance",
  workDescription: "",
  installationType: "solar",
};

const EMPTY_ADDRESS_FORM = {
  addressType: "fiscal" as "fiscal" | "billing" | "correspondence" | "delivery" | "other",
  line1: "",
  postalCode: "",
  city: "",
  state: "",
  isPrimary: true,
};

const EMPTY_CONTACT_FORM = {
  displayName: "",
  firstName: "",
  lastName: "",
  jobTitle: "",
  primaryEmail: "",
  primaryPhone: "",
  role: "other" as (typeof CANONICAL_CONTACT_CATEGORIES)[number],
  isPrimary: true,
  visibleToTechnicians: false,
  notes: "",
};

const EMPTY_SITE_FORM = {
  name: "",
  addressLine1: "",
  city: "",
  postalCode: "",
  role: "customer" as "owner" | "operator" | "occupier" | "maintainer" | "customer" | "other",
};

const EMPTY_RELATIONSHIP_FORM = {
  targetPartnerId: "",
  relationshipType: "member_of_group" as "parent_of" | "member_of_group" | "member_of_ute" | "represented_by" | "successor_of" | "other",
  notes: "",
};

const DIRECT_WORK_LONG_SELECT_TRIGGER_CLASS =
  "w-full min-w-0 max-w-full whitespace-normal data-[size=default]:h-auto data-[size=default]:!h-auto min-h-9 [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:whitespace-normal [&_[data-slot=select-value]]:line-clamp-none [&_[data-slot=select-value]]:flex-wrap [&_[data-slot=select-value]]:[overflow-wrap:anywhere]";
const DIRECT_WORK_LONG_SELECT_VALUE_CLASS =
  "min-w-0 flex-1 !line-clamp-none whitespace-normal [overflow-wrap:anywhere]";
const DIRECT_WORK_LONG_SELECT_CONTENT_CLASS =
  "w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)] min-w-0 [&_[data-radix-select-viewport]]:w-full [&_[data-radix-select-viewport]]:min-w-0 [&_[data-radix-select-viewport]]:max-w-full [&_[data-slot=select-item]]:whitespace-normal [&_[data-slot=select-item]]:[overflow-wrap:anywhere] [&_[data-slot=select-item]>span[id^='radix-']]:min-w-0 [&_[data-slot=select-item]>span[id^='radix-']]:whitespace-normal [&_[data-slot=select-item]>span[id^='radix-']]:[overflow-wrap:anywhere]";

export default function CustomerDetail() {
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const [, params] = useRoute("/customers/:partnerId");
  const partnerId = params?.partnerId ? parseInt(params.partnerId, 10) : null;
  const [newInstallationOpen, setNewInstallationOpen] = useState(false);
  const [directAcknowledged, setDirectAcknowledged] = useState(false);
  const [newContractOpen, setNewContractOpen] = useState(false);
  const [installationForm, setInstallationForm] = useState(EMPTY_INSTALLATION_FORM);
  const [addressOpen, setAddressOpen] = useState(false);
  const [addressForm, setAddressForm] = useState(EMPTY_ADDRESS_FORM);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT_FORM);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<any | null>(null);
  const [editContactForm, setEditContactForm] = useState({
    displayName: "",
    firstName: "",
    lastName: "",
    jobTitle: "",
    primaryEmail: "",
    primaryPhone: "",
    visibleToTechnicians: false,
    notes: "",
  });
  const [deleteContactConfirm, setDeleteContactConfirm] = useState<any | null>(null);
  const [siteOpen, setSiteOpen] = useState(false);
  const [siteForm, setSiteForm] = useState(EMPTY_SITE_FORM);
  const [site360Id, setSite360Id] = useState<number | null>(null);
  const [relationshipOpen, setRelationshipOpen] = useState(false);
  const [relationshipForm, setRelationshipForm] = useState(EMPTY_RELATIONSHIP_FORM);
  const [createInstallationError, setCreateInstallationError] = useState<string | null>(null);
  const [masterDataError, setMasterDataError] = useState<string | null>(null);
  const canSeeDQ = user ? DATA_QUALITY_ROLES.includes(user.role) : false;
  const locationWithSearch = search ? `${location}?${search}` : location;
  const tabFromLocation = getCustomerDetailTabFromLocation(locationWithSearch, canSeeDQ);
  const [activeTab, setActiveTab] = useState<CustomerDetailTab>(tabFromLocation);
  const utils = trpc.useUtils();

  useEffect(() => {
    setActiveTab(tabFromLocation);
  }, [tabFromLocation]);

  function handleTabChange(value: string) {
    if (!isCustomerDetailTab(value, canSeeDQ)) return;
    const nextTab = value;
    setActiveTab(nextTab);
    const nextLocation = locationWithCustomerDetailTab(locationWithSearch, nextTab);
    if (nextLocation !== locationWithSearch) setLocation(nextLocation, { replace: true });
  }

  const permissions = trpc.customers.permissions.useQuery(undefined, { enabled: !!user });

  const query360 = trpc.customers.get360.useQuery(
    { partnerId: partnerId! },
    { enabled: !!partnerId && !!user },
  );

  const customerContracts = trpc.customers.contracts.listForCustomer.useQuery(
    { customerPartnerId: partnerId! },
    { enabled: !!partnerId && !!user && newInstallationOpen },
  );

  const previousJourneyCustomer = useRef(partnerId);
  useEffect(() => {
    if (previousJourneyCustomer.current === partnerId) return;
    previousJourneyCustomer.current = partnerId;
    setInstallationForm(EMPTY_INSTALLATION_FORM);
    setDirectAcknowledged(false);
    setNewContractOpen(false);
    setNewInstallationOpen(false);
    setSite360Id(null);
  }, [partnerId]);

  useNewWorkIntent({
    customerId: partnerId,
    customer: query360.data,
    canCreate: !isNewWorkSearch(search) && isDirectWorkSearch(search) ? permissions.data?.canCreateInstallation : permissions.data?.canCreateCustomer,
    failed: query360.isError || permissions.isError,
  }, kind => {
    if (kind === "exception") { setDirectAcknowledged(false); setNewInstallationOpen(true); }
    else setNewContractOpen(true);
  });

  const queryInstallations = trpc.customers.getInstallations.useQuery(
    { partnerId: partnerId! },
    { enabled: !!partnerId && !!user },
  );

  const queryDQ = trpc.customers.getDataQualityIssues.useQuery(
    { partnerId: partnerId! },
    { enabled: !!partnerId && !!user && canSeeDQ },
  );

  const createInstallation = trpc.customers.createInstallation.useMutation({
    onSuccess: () => {
      setNewInstallationOpen(false);
      setInstallationForm(EMPTY_INSTALLATION_FORM);
      setCreateInstallationError(null);
      if (partnerId) {
        utils.customers.getInstallations.invalidate({ partnerId });
        utils.customers.get360.invalidate({ partnerId });
      }
    },
    onError: (error) => setCreateInstallationError(error.message),
  });

  const addAddress = trpc.customers.addAddress.useMutation({
    onSuccess: () => {
      setAddressOpen(false);
      setAddressForm(EMPTY_ADDRESS_FORM);
      setMasterDataError(null);
      if (partnerId) utils.customers.get360.invalidate({ partnerId });
    },
    onError: (error) => setMasterDataError(error.message),
  });

  const createContactForCustomer = trpc.canonicalContacts.createForCustomer.useMutation({
    onSuccess: () => {
      setContactOpen(false);
      setContactForm(EMPTY_CONTACT_FORM);
      setMasterDataError(null);
      if (partnerId) utils.customers.get360.invalidate({ partnerId });
      utils.canonicalContacts.resolveForInstallation.invalidate();
    },
    onError: (error) => setMasterDataError(error.message),
  });

  const addSite = trpc.customers.addSite.useMutation({
    onSuccess: () => {
      setSiteOpen(false);
      setSiteForm(EMPTY_SITE_FORM);
      setMasterDataError(null);
      if (partnerId) utils.customers.get360.invalidate({ partnerId });
    },
    onError: (error) => setMasterDataError(error.message),
  });

  const addRelationship = trpc.customers.addRelationship.useMutation({
    onSuccess: () => {
      setRelationshipOpen(false);
      setRelationshipForm(EMPTY_RELATIONSHIP_FORM);
      setMasterDataError(null);
      if (partnerId) utils.customers.get360.invalidate({ partnerId });
    },
    onError: (error) => setMasterDataError(error.message),
  });
  const updateAddress = trpc.customers.updateAddress.useMutation({
    onSuccess: () => { setMasterDataError(null); if (partnerId) utils.customers.get360.invalidate({ partnerId }); },
    onError: (error) => setMasterDataError(error.message),
  });
  const deactivateAddress = trpc.customers.deactivateAddress.useMutation({
    onSuccess: () => { setMasterDataError(null); if (partnerId) utils.customers.get360.invalidate({ partnerId }); },
    onError: (error) => setMasterDataError(error.message),
  });
  const setPrimaryAddress = trpc.customers.setPrimaryAddress.useMutation({
    onSuccess: () => { setMasterDataError(null); if (partnerId) utils.customers.get360.invalidate({ partnerId }); },
    onError: (error) => setMasterDataError(error.message),
  });
  const setPrimaryContact = trpc.canonicalContacts.setPrimary.useMutation({
    onSuccess: () => {
      setMasterDataError(null);
      if (partnerId) utils.customers.get360.invalidate({ partnerId });
      utils.canonicalContacts.resolveForInstallation.invalidate();
    },
    onError: (error) => setMasterDataError(error.message),
  });
  const deactivateContact = trpc.canonicalContacts.deactivate.useMutation({
    onSuccess: () => {
      setMasterDataError(null);
      if (partnerId) utils.customers.get360.invalidate({ partnerId });
      utils.canonicalContacts.resolveForInstallation.invalidate();
    },
    onError: (error) => setMasterDataError(error.message),
  });
  const reactivateContact = trpc.canonicalContacts.reactivate.useMutation({
    onSuccess: () => {
      setMasterDataError(null);
      if (partnerId) utils.customers.get360.invalidate({ partnerId });
      utils.canonicalContacts.resolveForInstallation.invalidate();
    },
    onError: (error) => setMasterDataError(error.message),
  });
  const updateContactMutation = trpc.canonicalContacts.update.useMutation({
    onSuccess: () => {
      setEditContactOpen(false);
      setEditingContact(null);
      setMasterDataError(null);
      if (partnerId) utils.customers.get360.invalidate({ partnerId });
      utils.canonicalContacts.resolveForInstallation.invalidate();
    },
    onError: (error) => setMasterDataError(error.message),
  });
  const updateSite = trpc.customers.updateSite.useMutation({
    onSuccess: () => { setMasterDataError(null); if (partnerId) utils.customers.get360.invalidate({ partnerId }); },
    onError: (error) => setMasterDataError(error.message),
  });
  const deactivateSite = trpc.customers.deactivateSite.useMutation({
    onSuccess: () => { setMasterDataError(null); if (partnerId) utils.customers.get360.invalidate({ partnerId }); },
    onError: (error) => setMasterDataError(error.message),
  });
  const finalizeRelationship = trpc.customers.finalizeRelationship.useMutation({
    onSuccess: () => { setMasterDataError(null); if (partnerId) utils.customers.get360.invalidate({ partnerId }); },
    onError: (error) => setMasterDataError(error.message),
  });

  function submitInstallation(e: FormEvent) {
    e.preventDefault();
    if (!partnerId) return;
    setCreateInstallationError(null);
    if (!canSubmitDirectWork(null, "exception", installationForm.workOrderType, directAcknowledged)) {
      setCreateInstallationError("Confirma que es una avería o mantenimiento sin contrato autorizado. Para una obra normal utiliza Nuevo contrato.");
      return;
    }
    createInstallation.mutate({
      partnerId,
      contractId: installationForm.contractId ? Number(installationForm.contractId) : null,
      siteId: installationForm.siteMode === "existing" && installationForm.siteId ? Number(installationForm.siteId) : null,
      newSite: installationForm.siteMode === "new" ? {
        name: installationForm.siteName,
        addressLine1: installationForm.siteAddressLine1,
        city: installationForm.siteCity || null,
        postalCode: installationForm.sitePostalCode || null,
        role: "customer",
      } : null,
      workOrderType: installationForm.workOrderType,
      workDescription: installationForm.workDescription || null,
      installationType: installationForm.installationType || "solar",
    });
  }

  function submitAddress(e: FormEvent) {
    e.preventDefault();
    if (!partnerId) return;
    setMasterDataError(null);
    addAddress.mutate({
      partnerId,
      addressType: addressForm.addressType,
      line1: addressForm.line1,
      postalCode: addressForm.postalCode || null,
      city: addressForm.city || null,
      state: addressForm.state || null,
      countryCode: "ES",
      isPrimary: addressForm.isPrimary,
    });
  }

  function submitContact(e: FormEvent) {
    e.preventDefault();
    if (!partnerId) return;
    setMasterDataError(null);
    createContactForCustomer.mutate({
      businessPartnerId: partnerId,
      displayName: contactForm.displayName,
      firstName: contactForm.firstName || null,
      lastName: contactForm.lastName || null,
      jobTitle: contactForm.jobTitle || null,
      primaryEmail: contactForm.primaryEmail || null,
      primaryPhone: contactForm.primaryPhone || null,
      role: contactForm.role,
      isPrimary: contactForm.isPrimary,
      visibleToTechnicians: contactForm.visibleToTechnicians,
      notes: contactForm.notes || null,
    });
  }

  function openEditContact(contact: any) {
    setEditingContact(contact);
    setEditContactForm({
      displayName: contact.displayName ?? "",
      firstName: (contact.firstName as string) ?? "",
      lastName: (contact.lastName as string) ?? "",
      jobTitle: contact.jobTitle ?? "",
      primaryEmail: contact.primaryEmail ?? "",
      primaryPhone: contact.primaryPhone ?? "",
      visibleToTechnicians: contact.visibleToTechnicians ?? false,
      notes: (contact.notes as string) ?? "",
    });
    setEditContactOpen(true);
  }

  function submitEditContact(e: FormEvent) {
    e.preventDefault();
    if (!partnerId || !editingContact) return;
    setMasterDataError(null);
    updateContactMutation.mutate({
      assignmentId: editingContact.assignmentId,
      businessPartnerId: partnerId,
      displayName: editContactForm.displayName.trim(),
      firstName: editContactForm.firstName.trim() || null,
      lastName: editContactForm.lastName.trim() || null,
      jobTitle: editContactForm.jobTitle.trim() || null,
      primaryEmail: editContactForm.primaryEmail.trim() || null,
      primaryPhone: editContactForm.primaryPhone.trim() || null,
      visibleToTechnicians: editContactForm.visibleToTechnicians,
      notes: editContactForm.notes.trim() || null,
    });
  }

  function submitSite(e: FormEvent) {
    e.preventDefault();
    if (!partnerId) return;
    setMasterDataError(null);
    addSite.mutate({
      partnerId,
      name: siteForm.name,
      addressLine1: siteForm.addressLine1,
      city: siteForm.city || null,
      postalCode: siteForm.postalCode || null,
      countryCode: "ES",
      role: siteForm.role,
    });
  }

  function submitRelationship(e: FormEvent) {
    e.preventDefault();
    if (!partnerId) return;
    setMasterDataError(null);
    addRelationship.mutate({
      sourcePartnerId: partnerId,
      targetPartnerId: Number(relationshipForm.targetPartnerId),
      relationshipType: relationshipForm.relationshipType,
      notes: relationshipForm.notes || null,
    });
  }

  if (!user || !partnerId) return null;

  if (query360.isLoading) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="h-6 w-48 bg-muted animate-pulse rounded mb-4" />
          <div className="h-48 bg-muted animate-pulse rounded" />
        </div>
      </DashboardLayout>
    );
  }

  if (query360.isError || !query360.data) {
    const is404 = isNotFoundQueryError(query360.error);
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-3 text-muted-foreground">
          <AlertCircle className="w-8 h-8" />
          <p className="text-sm">{is404 ? "Cliente no encontrado." : "Error al cargar el cliente."}</p>
          <Button variant="ghost" size="sm" onClick={() => setLocation("/customers")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver a Clientes
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const c = query360.data;
  const activePrimaryContacts = (c.contacts ?? []).filter(
    (contact) => contact.isPrimary && contact.validUntil == null,
  );
  const canonicalPrimaryContact =
    activePrimaryContacts.find((contact) => contact.scope === "customer") ??
    activePrimaryContacts[0] ??
    null;
  const completeness = Math.max(
    0,
    100 - (queryDQ.data ?? []).reduce((sum, issue) => sum + (DQ_PENALTIES[issue.issueType] ?? 0), 0),
  );

  return (
    <DashboardLayout>
      <main className="customer-detail-shell mx-auto max-w-7xl space-y-6 p-4 md:p-6">
        {/* Header */}
        <header className="flex min-w-0 flex-col gap-3 border-b border-border/70 pb-5 xl:flex-row xl:flex-wrap xl:items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/customers")}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Clientes
          </Button>
          <div data-testid="customer-detail-identity" className="flex w-full min-w-0 flex-col gap-1 xl:w-auto xl:min-w-0 xl:flex-1">
            <div className="flex min-w-0 items-start gap-2">
              <Building2 className="mt-1 w-5 h-5 shrink-0" />
              <h1 className="min-w-0 max-w-full text-2xl font-semibold tracking-tight break-words">{c.partyType === "organization" && c.tradeName?.trim() ? c.tradeName : c.legalName}</h1>
            </div>
            {c.partyType === "organization" && c.tradeName?.trim() && c.tradeName.trim() !== c.legalName && (
              <span className="min-w-0 max-w-full pl-7 text-sm text-muted-foreground [overflow-wrap:anywhere]">{c.legalName}</span>
            )}
            <div className="flex min-w-0 flex-wrap gap-x-4 gap-y-1 pl-7 text-xs">
              <span className="font-mono [overflow-wrap:anywhere]">{c.customerCode ?? "Sin código cliente"}</span>
              <span className="font-mono text-muted-foreground [overflow-wrap:anywhere]">Ref. {c.partnerCode}</span>
            </div>
          </div>
          <div data-testid="customer-detail-actions" className="flex w-full min-w-0 flex-wrap items-center gap-2 xl:ml-auto xl:w-auto xl:shrink-0">
            {permissions.data?.canCreateCustomer && (
              <Button variant="outline" onClick={() => setLocation(`/customers/${partnerId}/edit`)}>
                Editar perfil
              </Button>
            )}
            {permissions.data?.canCreateCustomer && (
              <Button onClick={() => setNewContractOpen(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Nuevo contrato
              </Button>
            )}
            {permissions.data?.canCreateInstallation && (
              <Button variant="outline" onClick={() => { setDirectAcknowledged(false); setNewInstallationOpen(true); }}>
                <Plus className="w-4 h-4 mr-1" />
                Trabajo sin contrato
              </Button>
            )}
            {c.openDqCount > 0 && (
              <Badge variant="secondary">
                {c.openDqCount} aviso{c.openDqCount !== 1 ? "s" : ""} de calidad
              </Badge>
            )}
          </div>
        </header>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="perfil">Perfil</TabsTrigger>
            <TabsTrigger value="contactos">
              Contactos {c.contacts.length > 0 && `(${c.contacts.length})`}
            </TabsTrigger>
            <TabsTrigger value="direcciones">
              Direcciones {c.addresses.length > 0 && `(${c.addresses.length})`}
            </TabsTrigger>
            <TabsTrigger value="sedes">
              Sedes / Ubicaciones {c.sites.length > 0 && `(${c.sites.length})`}
            </TabsTrigger>
            <TabsTrigger value="instalaciones">
              Instalaciones {!queryInstallations.isLoading && `(${queryInstallations.data?.length ?? 0})`}
            </TabsTrigger>
            <TabsTrigger value="contratos">Contratos</TabsTrigger>
            <TabsTrigger value="relaciones">
              Relaciones {c.relationships.length > 0 && `(${c.relationships.length})`}
            </TabsTrigger>
            {canSeeDQ && (
              <TabsTrigger value="calidad">
                Calidad de datos
                {(queryDQ.data?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs px-1.5">{queryDQ.data!.length}</Badge>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Tab: Perfil */}
          <TabsContent value="perfil" className="mt-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Datos legales</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <DetailRow label="Tipo de entidad" value={label(ENTITY_LABELS, c.entityType)} />
                  <DetailRow label="Tipo" value={label(ENTITY_LABELS, c.partyType)} />
                  <DetailRow label="Nombre comercial" value={c.tradeName} />
                  <DetailRow label="NIF / CIF" value={c.taxId} />
                  <DetailRow label="País fiscal" value={c.taxCountryCode} />
                  <DetailRow label="Validación fiscal" value={label(STATUS_LABELS, c.taxIdValidationStatus)} />
                  <DetailRow label="Estado del registro" value={label(STATUS_LABELS, c.recordStatus)} />
                  <DetailRow label="Alta" value={c.createdAt ? new Date(c.createdAt).toLocaleDateString("es-ES") : undefined} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Dirección fiscal</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {(() => {
                    const fiscal = c.addresses.find((address) => address.addressType === "fiscal" && address.isActive);
                    return fiscal ? (
                      <>
                        <p className="text-sm">{fiscal.line1 ?? "—"}</p>
                        {fiscal.line2 && <p className="text-sm text-muted-foreground">{fiscal.line2}</p>}
                        <p className="text-sm text-muted-foreground">{[fiscal.postalCode, fiscal.city, fiscal.state, fiscal.countryCode].filter(Boolean).join(" · ") || "—"}</p>
                      </>
                    ) : <p className="text-sm text-muted-foreground">Sin dirección fiscal activa.</p>;
                  })()}
                  {permissions.data?.canCreateCustomer && (
                    <Button size="sm" variant="outline" onClick={() => handleTabChange("direcciones")}>Gestionar direcciones</Button>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Perfil comercial</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <DetailRow label="Código cliente" value={c.customerCode} />
                  <DetailRow label="Referencia técnica" value={c.partnerCode} />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground">Estado</span>
                    <LifecycleBadge stage={c.lifecycleStage} />
                  </div>
                  <DetailRow label="Cliente desde" value={c.customerSince} />
                  <DetailRow label="Resp. comercial" value={c.salesOwnerName} />
                  <DetailRow label="Pago por defecto" value={label(PAYMENT_LABELS, c.defaultPaymentMethod)} />
                  <DetailRow label="Plazo de pago (días)" value={c.defaultPaymentTermDays} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Contacto principal</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {canonicalPrimaryContact ? (
                    <>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span>{canonicalPrimaryContact.displayName}</span>
                        {canonicalPrimaryContact.jobTitle && (
                          <span className="text-xs text-muted-foreground font-normal">({canonicalPrimaryContact.jobTitle})</span>
                        )}
                      </div>
                      {canonicalPrimaryContact.primaryEmail && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span>{canonicalPrimaryContact.primaryEmail}</span>
                        </div>
                      )}
                      {canonicalPrimaryContact.primaryPhone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span>{canonicalPrimaryContact.primaryPhone}</span>
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground pt-1">
                        Ámbito: {CONTACT_SCOPE_LABELS[canonicalPrimaryContact.scope]} · {canonicalPrimaryContact.role === "primary" ? "Principal (legado)" : label(ROLE_LABELS, canonicalPrimaryContact.role)}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Sin contacto principal asignado.</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Canales generales</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {c.generalEmail ? (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span>{c.generalEmail}</span>
                    </div>
                  ) : null}
                  {c.generalPhone ? (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span>{c.generalPhone}</span>
                    </div>
                  ) : null}
                  {!c.generalEmail && !c.generalPhone && (
                    <p className="text-sm text-muted-foreground">Sin canales generales registrados.</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Roles activos</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-1.5">
                  {c.roles.map(r => (
                    <Badge key={r} variant="secondary">{label(ROLE_LABELS, r)}</Badge>
                  ))}
                  {c.roles.length === 0 && (
                    <p className="text-sm text-muted-foreground">Sin roles activos.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Contactos */}
          <TabsContent value="contactos" className="mt-4">
            <div className="mb-3 flex justify-end">
              {permissions.data?.canCreateCustomer && (
                <Button size="sm" onClick={() => setContactOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir contacto
                </Button>
              )}
            </div>
            {c.contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No hay contactos. Añade el primer contacto.</p>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead>Ámbito</TableHead>
                        <TableHead>Categoría / Cargo</TableHead>
                        <TableHead>Visible técnicos</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Principal</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                        <TableBody>
                      {c.contacts.map(contact => {
                        const contactIsActive = contact.validUntil == null;
                        return (
                        <TableRow key={contact.assignmentId}>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5 text-muted-foreground" />
                              {contact.displayName}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{contact.primaryEmail ?? "—"}</TableCell>
                          <TableCell className="text-sm">{contact.primaryPhone ?? "—"}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{CONTACT_SCOPE_LABELS[contact.scope]}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div>{contact.role === "primary" ? "Principal (legado)" : label(ROLE_LABELS, contact.role)}</div>
                            {contact.jobTitle && <div className="text-xs text-muted-foreground">{contact.jobTitle}</div>}
                          </TableCell>
                          <TableCell className="text-sm">
                            <Switch
                              checked={contact.visibleToTechnicians}
                              disabled={!contactIsActive || updateContactMutation.isPending}
                              onCheckedChange={(checked) => {
                                updateContactMutation.mutate({
                                  assignmentId: contact.assignmentId,
                                  businessPartnerId: partnerId!,
                                  visibleToTechnicians: checked,
                                });
                              }}
                            />
                          </TableCell>
                          <TableCell className="text-sm">
                            {contact.validUntil == null ? "Activo" : `Inactivo${contact.validUntil ? ` · ${contact.validUntil}` : ""}`}
                          </TableCell>
                          <TableCell>
                            {contactIsActive && contact.isPrimary
                              ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                              : contactIsActive && permissions.data?.canCreateCustomer ? (
                                  <Button size="sm" variant="ghost"
                                    onClick={() => setPrimaryContact.mutate({ businessPartnerId: partnerId!, assignmentId: contact.assignmentId })}
                                    disabled={setPrimaryContact.isPending}
                                  >
                                    Hacer principal
                                  </Button>
                                ) : <span className="text-sm text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 justify-end">
                              {contactIsActive && permissions.data?.canCreateCustomer ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditContact(contact)}
                                  >
                                    Editar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-destructive"
                                    onClick={() => setDeleteContactConfirm(contact)}
                                  >
                                    Eliminar
                                  </Button>
                                </>
                              ) : !contactIsActive && permissions.data?.canCreateCustomer ? (
                                <Button size="sm" variant="outline"
                                  onClick={() => reactivateContact.mutate({ businessPartnerId: partnerId!, assignmentId: contact.assignmentId })}
                                  disabled={reactivateContact.isPending}
                                >
                                  Reactivar
                                </Button>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab: Direcciones */}
          <TabsContent value="direcciones" className="mt-4">
            <div className="mb-3 flex justify-end">
              {permissions.data?.canCreateCustomer && (
                <Button size="sm" onClick={() => setAddressOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir dirección
                </Button>
              )}
            </div>
            {c.addresses.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No hay direcciones. Añade la dirección fiscal.</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {c.addresses.map(addr => (
                  <Card key={addr.id}>
                    <CardContent className="pt-4 space-y-1">
                      <div className="flex items-center gap-1.5 justify-between">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-sm font-medium">{label(ADDRESS_LABELS, addr.addressType)}</span>
                        </div>
                        <div className="flex gap-1">
                          {addr.isPrimary && <Badge variant="secondary" className="text-xs">Principal</Badge>}
                          {!addr.isActive && <Badge variant="outline" className="text-xs">Inactiva</Badge>}
                        </div>
                      </div>
                      {addr.line1 && <p className="text-sm">{addr.line1}</p>}
                      <p className="text-sm text-muted-foreground">
                        {[addr.postalCode, addr.city, addr.countryCode].filter(Boolean).join(" · ")}
                      </p>
                      {permissions.data?.canCreateCustomer && addr.isActive && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {!addr.isPrimary && (
                            <Button size="sm" variant="ghost"
                              onClick={() => setPrimaryAddress.mutate({ partnerId: partnerId!, addressId: addr.id })}
                              disabled={setPrimaryAddress.isPending}
                            >
                              Hacer principal
                            </Button>
                          )}
                          <Button size="sm" variant="outline"
                            onClick={() => deactivateAddress.mutate({ partnerId: partnerId!, addressId: addr.id })}
                            disabled={deactivateAddress.isPending}
                          >
                            Desactivar
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sedes" className="mt-4">
            <div className="mb-3 flex justify-end">
              {permissions.data?.canCreateInstallation && (
                <Button size="sm" onClick={() => setSiteOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir sede
                </Button>
              )}
            </div>
            {c.sites.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No hay sedes. Crea la primera ubicación operativa.</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {c.sites.map((site) => (
                  <Card key={site.id}>
                    <CardContent className="pt-4 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{site.name}</span>
                        <Badge variant="secondary" className="font-mono text-xs">{site.siteCode}</Badge>
                      </div>
                      <p className="text-sm">{site.addressLine1 ?? "—"}</p>
                      <p className="text-sm text-muted-foreground">{[site.postalCode, site.city, site.countryCode].filter(Boolean).join(" · ")}</p>
                      <div className="flex gap-1">
                        <Badge variant="outline">{label(ROLE_LABELS, site.role)}</Badge>
                        {site.isPrimary && <Badge variant="secondary">Principal</Badge>}
                      </div>
                      <div className="pt-2">
                        <Button size="sm" variant="outline" onClick={() => setSite360Id(site.id)}>
                          Abrir Site 360
                        </Button>
                      </div>
                      {permissions.data?.canCreateInstallation && site.isActive && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          <Button size="sm" variant="outline"
                            onClick={() => deactivateSite.mutate({ partnerId: partnerId!, siteId: site.id })}
                            disabled={deactivateSite.isPending}
                          >
                            Desactivar
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tab: Instalaciones */}
          <TabsContent value="instalaciones" className="mt-4">
            <div className="mb-3 flex justify-end">
              {permissions.data?.canCreateInstallation && (
                <Button size="sm" onClick={() => { setDirectAcknowledged(false); setNewInstallationOpen(true); }}>
                  <Plus className="w-4 h-4 mr-1" />
                  Trabajo sin contrato
                </Button>
              )}
            </div>
            {queryInstallations.isLoading ? (
              <div className="h-24 bg-muted animate-pulse rounded" />
            ) : !queryInstallations.data?.length ? (
              <p className="text-sm text-muted-foreground text-center py-8">No hay instalaciones. Crea la primera instalación desde una sede.</p>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <Table className="hidden md:table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nº expediente</TableHead>
                        <TableHead>Instalación</TableHead>
                        <TableHead>Sede</TableHead>
                        <TableHead>Contrato</TableHead>
                        <TableHead>Dirección</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Principal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {queryInstallations.data.map(inst => (
                        <TableRow
                          key={inst.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setLocation(`/installations/${inst.id}`)}
                        >
                          <TableCell className="font-mono text-xs">{inst.expedienteCode ?? "—"}</TableCell>
                          <TableCell className="text-sm">{resolveInstallationTitle(inst)}</TableCell>
                          <TableCell className="text-sm">{inst.siteName ?? "Sede pendiente"}</TableCell>
                          <TableCell className="text-sm">{inst.contractCode ?? "Sin contrato"}</TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate">{inst.address ?? "—"}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{installationStatusLabel(inst.status)}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">{label(ROLE_LABELS, inst.role)}</TableCell>
                          <TableCell>
                            {inst.isPrimaryForRole && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="grid gap-2 p-3 md:hidden">
                    {queryInstallations.data.map(inst => (
                      <button
                        key={inst.id}
                        className="rounded-md border p-3 text-left"
                        onClick={() => setLocation(`/installations/${inst.id}`)}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs font-medium">{inst.expedienteCode ?? "Sin expediente"}</span>
                          <Badge variant="outline" className="text-xs">{installationStatusLabel(inst.status)}</Badge>
                        </div>
                        <div className="mt-2 text-sm">{inst.address ?? "—"}</div>
                        <div className="mt-1 text-sm font-medium">{resolveInstallationTitle(inst)}</div>
                        <div className="mt-1 text-xs text-muted-foreground">Sede: {inst.siteName ?? "pendiente"} · Contrato: {inst.contractCode ?? "sin contrato"}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{label(ROLE_LABELS, inst.role)}{inst.isPrimaryForRole ? " · principal" : ""}</div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab: Contratos */}
          <TabsContent value="contratos" className="mt-4">
            <ContractsTab customerPartnerId={partnerId ?? 0} />
          </TabsContent>

          {/* Tab: Relaciones */}
          <TabsContent value="relaciones" className="mt-4">
            <div className="mb-3 flex justify-end">
              {permissions.data?.canCreateCustomer && (
                <Button size="sm" onClick={() => setRelationshipOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir relación
                </Button>
              )}
            </div>
            {c.relationships.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No hay relaciones. Añade una relación empresarial.</p>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Dirección</TableHead>
                        <TableHead>Partner relacionado</TableHead>
                        <TableHead>Código</TableHead>
                        <TableHead>Válido desde</TableHead>
                        <TableHead>Válido hasta</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {c.relationships.map(rel => (
                        <TableRow key={rel.id}>
                          <TableCell className="text-sm">{label(RELATIONSHIP_LABELS, rel.relationshipType)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Link2 className="w-3 h-3" />
                              {rel.direction === "source" ? "→ objetivo" : "← origen"}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{rel.relatedLegalName}</TableCell>
                          <TableCell className="font-mono text-xs">{rel.relatedPartnerCode}</TableCell>
                          <TableCell className="text-sm">{rel.validFrom ?? "—"}</TableCell>
                          <TableCell className="text-sm">{rel.validUntil ?? "—"}</TableCell>
                          <TableCell className="text-right">
                            {permissions.data?.canCreateCustomer && !rel.validUntil && (
                              <Button size="sm" variant="outline"
                                onClick={() => finalizeRelationship.mutate({ relationshipId: rel.id })}
                                disabled={finalizeRelationship.isPending}
                              >
                                Finalizar
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab: Calidad de datos (admin / admin_manager only) */}
          {canSeeDQ && (
            <TabsContent value="calidad" className="mt-4">
              {queryDQ.isLoading ? (
                <div className="h-24 bg-muted animate-pulse rounded" />
              ) : (
                <div className="space-y-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium">Completitud de datos</p>
                          <p className="text-sm text-muted-foreground">
                            {queryDQ.data && queryDQ.data.length > 0
                              ? "La entidad necesita algunos datos para estar completa."
                              : "Calidad evaluada sin incidencias abiertas."}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-semibold">
                            {completeness}
                            <span className="text-sm text-muted-foreground">/100</span>
                          </span>
                          <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-green-600"
                              style={{ width: `${Math.max(0, Math.min(100, completeness))}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {queryDQ.data && queryDQ.data.length > 0 && (
                    (["bloqueo", "incidencia", "recomendacion"] as const).map((cat) => {
                      const issues = queryDQ.data!.filter((issue) =>
                        (DQ_CATALOG[issue.issueType]?.category ?? resolveDqCategory(issue.issueType)) === cat
                      );
                      if (issues.length === 0) return null;
                      return (
                        <div key={cat}>
                          <h3 className="mb-2 text-sm font-semibold">
                            <Badge variant={cat === "bloqueo" ? "destructive" : cat === "incidencia" ? "secondary" : "outline"}>{DQ_CATEGORIES[cat]}</Badge>
                          </h3>
                          <Card>
                            <CardContent className="p-0">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Incidencia</TableHead>
                                    <TableHead>Gravedad</TableHead>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead className="text-right">Acción</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {issues.map(issue => {
                                    const entry = DQ_CATALOG[issue.issueType] ?? resolveDqEntry(issue.issueType);
                                    const red = entry.redBadge && (c.lifecycleStage === "customer" || entry.category === "bloqueo");
                                    return (
                                      <TableRow key={issue.id}>
                                        <TableCell className="text-sm">
                                          <div className="flex items-center gap-2">
                                            {red && <span className="h-2 w-2 rounded-full bg-red-500" />}
                                            <div>
                                              <div className="font-medium">{entry.title}</div>
                                              <div className="text-xs text-muted-foreground">{entry.explanation}</div>
                                            </div>
                                          </div>
                                        </TableCell>
                                        <TableCell><SeverityBadge severity={issue.severity} /></TableCell>
                                        <TableCell className="text-sm">
                                          {new Date(issue.createdAt).toLocaleDateString("es-ES")}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleTabChange(entry.target)}
                                          >
                                            {entry.action}
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </CardContent>
                          </Card>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </main>

      <Dialog open={newInstallationOpen} onOpenChange={setNewInstallationOpen}>
        <DialogContent data-customer-detail-scope="true" className="max-h-[92vh] max-w-xl overflow-y-auto [&>form]:min-w-0 [&>form]:max-w-full">
          <DialogHeader>
            <DialogTitle>Trabajo sin contrato</DialogTitle>
            <DialogDescription>Solo avería o mantenimiento autorizado. Para una obra normal crea un contrato y añade la instalación desde él.</DialogDescription>
          </DialogHeader>
          <form className="min-w-0 max-w-full space-y-4" onSubmit={submitInstallation}>
            <div className="space-y-1.5">
              <Label>Sede</Label>
              <Select value={installationForm.siteMode} onValueChange={(v: "existing" | "new") => setInstallationForm({ ...installationForm, siteMode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent data-customer-detail-scope="true">
                  <SelectItem value="existing">Seleccionar sede existente</SelectItem>
                  <SelectItem value="new">Crear nueva sede</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {installationForm.siteMode === "existing" ? (
              <div className="space-y-1.5">
                <Label>Sede existente</Label>
                <Select value={installationForm.siteId || "none"} onValueChange={(v) => setInstallationForm({ ...installationForm, siteId: v === "none" ? "" : v })}>
                  <SelectTrigger className={DIRECT_WORK_LONG_SELECT_TRIGGER_CLASS}>
                    <SelectValue className={DIRECT_WORK_LONG_SELECT_VALUE_CLASS} placeholder="Selecciona sede" />
                  </SelectTrigger>
                  <SelectContent data-customer-detail-scope="true" className={DIRECT_WORK_LONG_SELECT_CONTENT_CLASS}>
                    <SelectItem value="none">Selecciona sede</SelectItem>
                    {c.sites.map((site) => (
                      <SelectItem key={site.id} value={String(site.id)}>{site.siteCode} · {site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Nombre de sede</Label>
                  <Input value={installationForm.siteName} onChange={e => setInstallationForm({ ...installationForm, siteName: e.target.value })} required />
                </div>
                <div className="space-y-1.5">
                  <Label>Dirección de sede</Label>
                  <Input value={installationForm.siteAddressLine1} onChange={e => setInstallationForm({ ...installationForm, siteAddressLine1: e.target.value })} required />
                </div>
                <div className="space-y-1.5">
                  <Label>Municipio</Label>
                  <Input value={installationForm.siteCity} onChange={e => setInstallationForm({ ...installationForm, siteCity: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Código postal</Label>
                  <Input value={installationForm.sitePostalCode} onChange={e => setInstallationForm({ ...installationForm, sitePostalCode: e.target.value })} />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Contrato asociado (opcional)</Label>
              <Select value={installationForm.contractId || "none"} onValueChange={(v) => setInstallationForm({ ...installationForm, contractId: v === "none" ? "" : v })}>
                <SelectTrigger className={DIRECT_WORK_LONG_SELECT_TRIGGER_CLASS}>
                  <SelectValue className={DIRECT_WORK_LONG_SELECT_VALUE_CLASS} placeholder="Sin contrato asociado" />
                </SelectTrigger>
                <SelectContent data-customer-detail-scope="true" className={DIRECT_WORK_LONG_SELECT_CONTENT_CLASS}>
                  <SelectItem value="none">Sin contrato asociado</SelectItem>
                  {(customerContracts.data ?? []).map((contract) => (
                    <SelectItem key={contract.id} value={String(contract.id)}>
                      {contract.contractCode} · {contract.title} · {STATUS_LABELS[contract.status] ?? contract.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {customerContracts.isError && <p className="text-xs text-muted-foreground">No se pudieron cargar contratos; puedes continuar sin vincular uno.</p>}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Tipo de orden</Label>
                <Select value={installationForm.workOrderType} onValueChange={(v: typeof installationForm.workOrderType) => setInstallationForm({ ...installationForm, workOrderType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent data-customer-detail-scope="true">
                    <SelectItem value="breakdown">Avería</SelectItem>
                    <SelectItem value="maintenance">Mantenimiento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo instalación</Label>
                <Input value={installationForm.installationType} onChange={e => setInstallationForm({ ...installationForm, installationType: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Descripción</Label>
              <Textarea value={installationForm.workDescription} onChange={e => setInstallationForm({ ...installationForm, workDescription: e.target.value })} />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={directAcknowledged} onChange={event => setDirectAcknowledged(event.target.checked)} />
              Confirmo que es una avería o mantenimiento sin contrato autorizado.
            </label>
            {createInstallationError && <p className="text-sm text-destructive">{createInstallationError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewInstallationOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={createInstallation.isPending}>
                {createInstallation.isPending ? "Creando..." : "Crear instalación"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={addressOpen} onOpenChange={setAddressOpen}>
        <DialogContent data-customer-detail-scope="true">
          <DialogHeader><DialogTitle>Añadir dirección</DialogTitle></DialogHeader>
          <form className="space-y-3" onSubmit={submitAddress}>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={addressForm.addressType} onValueChange={(v: typeof addressForm.addressType) => setAddressForm({ ...addressForm, addressType: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent data-customer-detail-scope="true">
                  {Object.entries(ADDRESS_LABELS).map(([value, text]) => <SelectItem key={value} value={value}>{text}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Dirección</Label><Input value={addressForm.line1} onChange={(e) => setAddressForm({ ...addressForm, line1: e.target.value })} required /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Código postal</Label><Input value={addressForm.postalCode} onChange={(e) => setAddressForm({ ...addressForm, postalCode: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Municipio</Label><Input value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Provincia</Label><Input value={addressForm.state} onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={addressForm.isPrimary} onChange={(e) => setAddressForm({ ...addressForm, isPrimary: e.target.checked })} /> Principal</label>
            {masterDataError && <p className="text-sm text-destructive">{masterDataError}</p>}
            <DialogFooter><Button type="button" variant="outline" onClick={() => setAddressOpen(false)}>Cancelar</Button><Button type="submit" disabled={addAddress.isPending}>Guardar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent data-customer-detail-scope="true">
          <DialogHeader><DialogTitle>Añadir contacto</DialogTitle></DialogHeader>
          <form className="space-y-3" onSubmit={submitContact}>
            <div className="space-y-1.5"><Label>Nombre visible</Label><Input value={contactForm.displayName} onChange={(e) => setContactForm({ ...contactForm, displayName: e.target.value })} required /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Nombre</Label><Input value={contactForm.firstName} onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Apellidos</Label><Input value={contactForm.lastName} onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Cargo</Label><Input value={contactForm.jobTitle} onChange={(e) => setContactForm({ ...contactForm, jobTitle: e.target.value })} /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Email</Label><Input value={contactForm.primaryEmail} onChange={(e) => setContactForm({ ...contactForm, primaryEmail: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Teléfono</Label><Input value={contactForm.primaryPhone} onChange={(e) => setContactForm({ ...contactForm, primaryPhone: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Categoría</Label>
              <Select value={contactForm.role} onValueChange={(v: typeof contactForm.role) => setContactForm({ ...contactForm, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent data-customer-detail-scope="true">
                  {CANONICAL_CONTACT_CATEGORIES.map((value) => <SelectItem key={value} value={value}>{label(ROLE_LABELS, value)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={contactForm.isPrimary} onChange={(e) => setContactForm({ ...contactForm, isPrimary: e.target.checked })} /> Contacto principal</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={contactForm.visibleToTechnicians} onChange={(e) => setContactForm({ ...contactForm, visibleToTechnicians: e.target.checked })} /> Visible para técnicos</label>
            <div className="space-y-1.5"><Label>Notas</Label><Textarea value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} rows={3} /></div>
            {masterDataError && <p className="text-sm text-destructive">{masterDataError}</p>}
            <DialogFooter><Button type="button" variant="outline" onClick={() => setContactOpen(false)}>Cancelar</Button><Button type="submit" disabled={createContactForCustomer.isPending}>Guardar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editContactOpen} onOpenChange={setEditContactOpen}>
        <DialogContent data-customer-detail-scope="true">
          <DialogHeader><DialogTitle>Editar contacto</DialogTitle></DialogHeader>
          <form className="space-y-3" onSubmit={submitEditContact}>
            <div className="space-y-1.5"><Label>Nombre visible</Label><Input value={editContactForm.displayName} onChange={(e) => setEditContactForm({ ...editContactForm, displayName: e.target.value })} required /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Nombre</Label><Input value={editContactForm.firstName} onChange={(e) => setEditContactForm({ ...editContactForm, firstName: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Apellidos</Label><Input value={editContactForm.lastName} onChange={(e) => setEditContactForm({ ...editContactForm, lastName: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Cargo</Label><Input value={editContactForm.jobTitle} onChange={(e) => setEditContactForm({ ...editContactForm, jobTitle: e.target.value })} /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Email</Label><Input value={editContactForm.primaryEmail} onChange={(e) => setEditContactForm({ ...editContactForm, primaryEmail: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Teléfono</Label><Input value={editContactForm.primaryPhone} onChange={(e) => setEditContactForm({ ...editContactForm, primaryPhone: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={editContactForm.visibleToTechnicians} onCheckedChange={(checked) => setEditContactForm({ ...editContactForm, visibleToTechnicians: checked })} id="edit-contact-visible" />
              <Label htmlFor="edit-contact-visible" className="cursor-pointer text-sm">Visible para técnicos</Label>
            </div>
            <div className="space-y-1.5"><Label>Notas</Label><Textarea value={editContactForm.notes} onChange={(e) => setEditContactForm({ ...editContactForm, notes: e.target.value })} rows={3} /></div>
            {masterDataError && <p className="text-sm text-destructive">{masterDataError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditContactOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={updateContactMutation.isPending}>Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteContactConfirm != null} onOpenChange={(open) => !open && setDeleteContactConfirm(null)}>
        <DialogContent data-customer-detail-scope="true">
          <DialogHeader>
            <DialogTitle>¿Eliminar este contacto?</DialogTitle>
            <DialogDescription className="space-y-1 pt-2">
              <span>Dejará de mostrarse en este ámbito y en sus herencias.</span>
              <br />
              <span>El historial se conservará.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteContactConfirm(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deactivateContact.isPending}
              onClick={() => {
                if (deleteContactConfirm && partnerId) {
                  deactivateContact.mutate(
                    { businessPartnerId: partnerId, assignmentId: deleteContactConfirm.assignmentId },
                    {
                      onSuccess: () => {
                        setDeleteContactConfirm(null);
                      },
                    },
                  );
                }
              }}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={siteOpen} onOpenChange={setSiteOpen}>
        <DialogContent data-customer-detail-scope="true">
          <DialogHeader><DialogTitle>Añadir sede</DialogTitle></DialogHeader>
          <form className="space-y-3" onSubmit={submitSite}>
            <div className="space-y-1.5"><Label>Nombre</Label><Input value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} required /></div>
            <div className="space-y-1.5"><Label>Dirección</Label><Input value={siteForm.addressLine1} onChange={(e) => setSiteForm({ ...siteForm, addressLine1: e.target.value })} required /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Municipio</Label><Input value={siteForm.city} onChange={(e) => setSiteForm({ ...siteForm, city: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Código postal</Label><Input value={siteForm.postalCode} onChange={(e) => setSiteForm({ ...siteForm, postalCode: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Rol del cliente</Label>
              <Select value={siteForm.role} onValueChange={(v: typeof siteForm.role) => setSiteForm({ ...siteForm, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent data-customer-detail-scope="true">{["owner", "operator", "occupier", "maintainer", "customer", "other"].map((value) => <SelectItem key={value} value={value}>{label(ROLE_LABELS, value)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {masterDataError && <p className="text-sm text-destructive">{masterDataError}</p>}
            <DialogFooter><Button type="button" variant="outline" onClick={() => setSiteOpen(false)}>Cancelar</Button><Button type="submit" disabled={addSite.isPending}>Guardar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={relationshipOpen} onOpenChange={setRelationshipOpen}>
        <DialogContent data-customer-detail-scope="true">
          <DialogHeader><DialogTitle>Añadir relación</DialogTitle></DialogHeader>
          <form className="space-y-3" onSubmit={submitRelationship}>
            <div className="space-y-1.5"><Label>Partner relacionado</Label><Input type="number" min={1} value={relationshipForm.targetPartnerId} onChange={(e) => setRelationshipForm({ ...relationshipForm, targetPartnerId: e.target.value })} required /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={relationshipForm.relationshipType} onValueChange={(v: typeof relationshipForm.relationshipType) => setRelationshipForm({ ...relationshipForm, relationshipType: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent data-customer-detail-scope="true">{Object.entries(RELATIONSHIP_LABELS).map(([value, text]) => <SelectItem key={value} value={value}>{text}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Notas</Label><Textarea value={relationshipForm.notes} onChange={(e) => setRelationshipForm({ ...relationshipForm, notes: e.target.value })} /></div>
            {masterDataError && <p className="text-sm text-destructive">{masterDataError}</p>}
            <DialogFooter><Button type="button" variant="outline" onClick={() => setRelationshipOpen(false)}>Cancelar</Button><Button type="submit" disabled={addRelationship.isPending}>Guardar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Site360Dialog
        partnerId={partnerId}
        site={c.sites.find((site) => site.id === site360Id) ?? null}
        open={site360Id != null}
        onOpenChange={(open) => { if (!open) setSite360Id(null); }}
        canManage={permissions.data?.canCreateInstallation ?? false}
      />

      <NewContractDialog customerPartnerId={partnerId ?? 0} open={newContractOpen} onOpenChange={setNewContractOpen} canWriteEconomic={user?.role === "admin" || user?.role === "admin_manager"} />
    </DashboardLayout>
  );
}
